import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { formatCurrency, formatPercentage, formatNumber } from '@/lib/utils/format';
import type { WinLossData } from '../types/analytics.types';

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  color: 'hsl(var(--foreground))',
};

const PIE_COLORS = {
  won: '#10b981',
  lost: '#ef4444',
  noDecision: '#94a3b8',
};

interface WinLossTabProps {
  data: WinLossData | null;
  isLoading: boolean;
}

export function WinLossTab({ data, isLoading }: WinLossTabProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-20" />
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-[300px] w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-[300px] w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const pieData = [
    { name: 'Ganadas', value: data.won, color: PIE_COLORS.won },
    { name: 'Perdidas', value: data.lost, color: PIE_COLORS.lost },
    { name: 'Sin Decision', value: data.noDecision, color: PIE_COLORS.noDecision },
  ].filter((d) => d.value > 0);

  const revenueCompare = [
    { name: 'Ingresos', ganadas: data.revenueWon, perdidas: data.revenueLost },
  ];

  const marginCompare = [
    { name: 'Margen Prom.', ganadas: data.avgMarginWon, perdidas: data.avgMarginLost },
  ];

  const kpis = [
    { label: 'Tasa de Ganancia', value: formatPercentage(data.winRate), color: 'text-green-600' },
    { label: 'Cotizaciones Ganadas', value: formatNumber(data.won, 0), color: 'text-emerald-600' },
    { label: 'Cotizaciones Perdidas', value: formatNumber(data.lost, 0), color: 'text-red-600' },
    { label: 'Sin Decision', value: formatNumber(data.noDecision, 0), color: 'text-slate-600' },
  ];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="p-4">
            <p className="text-xs text-muted-foreground">{kpi.label}</p>
            <p className={`text-2xl font-bold mt-1 ${kpi.color}`}>{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ganadas y perdidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  Sin datos de ganancia/perdida
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Comparative Bars */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Comparativa Ganadas vs Perdidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Revenue comparison */}
              <div>
                <p className="text-sm font-medium mb-2">Ingresos</p>
                <div className="h-[120px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueCompare} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        className="fill-muted-foreground"
                        tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        className="fill-muted-foreground"
                        width={80}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={
                          ((value: any, name: string) => [
                            formatCurrency(Number(value ?? 0)),
                            name === 'ganadas' ? 'Ganadas' : 'Perdidas',
                          ]) as any
                        }
                      />
                      <Bar dataKey="ganadas" fill="#10b981" radius={[0, 4, 4, 0]} name="ganadas" />
                      <Bar
                        dataKey="perdidas"
                        fill="#ef4444"
                        radius={[0, 4, 4, 0]}
                        name="perdidas"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Margin comparison */}
              <div>
                <p className="text-sm font-medium mb-2">Margen Promedio</p>
                <div className="h-[120px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={marginCompare} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11 }}
                        className="fill-muted-foreground"
                        tickFormatter={(v) => `${v.toFixed(0)}%`}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        className="fill-muted-foreground"
                        width={80}
                      />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={
                          ((value: any, name: string) => [
                            formatPercentage(Number(value ?? 0)),
                            name === 'ganadas' ? 'Ganadas' : 'Perdidas',
                          ]) as any
                        }
                      />
                      <Bar dataKey="ganadas" fill="#10b981" radius={[0, 4, 4, 0]} name="ganadas" />
                      <Bar
                        dataKey="perdidas"
                        fill="#ef4444"
                        radius={[0, 4, 4, 0]}
                        name="perdidas"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
